/**
 * The browser's E2EE surface.
 *
 * Import from here, never from the modules underneath: the split between
 * store, keys and toDevice is internal, and the order they run in matters.
 */
import { loadCrypto } from '@nsibi/e2ee-wasm';

import { bootstrapIdentity, type Identity } from './keys';
import { answerKeyRequest, requestHistoryFromOwnDevices } from './keyRequests';
import { drainToDevice, setKeyRequestHandler } from './toDevice';
import {
  clearCryptoStore,
  loadMegolmSession,
  pickleKey,
  saveMegolmSession,
  savePlaintext,
} from './store';

export { clearCryptoStore, loadPlaintexts } from './store';
export { requestMissingKeys, requestHistoryFromOwnDevices, type MissingKey } from './keyRequests';
export { onKeysImported } from './toDevice';
export type { Identity } from './keys';

let identity: Identity | null = null;
let starting: Promise<Identity> | null = null;

/**
 * Bring the crypto up: load the wasm core, unlock the pickle key, publish
 * this browser's identity, and drain whatever room keys are waiting.
 *
 * Safe to call repeatedly. Concurrent callers share one run rather than
 * racing to create two accounts for the same browser.
 */
export async function initCrypto(): Promise<Identity> {
  if (identity) return identity;
  if (!starting) {
    starting = (async () => {
      const id = await bootstrapIdentity();
      // Requests for keys we created are answered from here on. Set before
      // the first drain so one already waiting is not dropped.
      setKeyRequestHandler(answerKeyRequest);
      // Keys first, mailbox second. A key that arrived before we published
      // cannot exist, so there is no ordering to lose - but doing it the
      // other way round would drain an empty mailbox on every first run.
      await drainToDevice();
      identity = id;
      // Now that the phone can seal to us, ask it for everything it can
      // read. Once per browser; the phone answers on its next drain and the
      // keys land through the nudge. Best effort - a browser that cannot
      // reach the phone still works for everything sent from now on.
      void requestHistoryFromOwnDevices().catch((err) => {
        console.warn('[nsibi] history request not sent', err);
      });
      return id;
    })();
  }
  return starting;
}

export function cryptoIdentity(): Identity | null {
  return identity;
}

/** Pull any newly-shared room keys. Cheap, and worth doing before rendering
 *  a conversation whose messages we could not previously read.
 *
 *  Awaits ``initCrypto`` rather than no-opping when the account is not up
 *  yet: right after linking, a conversation can call this before bootstrap
 *  has finished, and a silent early return there left the keys the phone
 *  pushed at link time sitting unimported - the transcript stayed locked
 *  until a reload. */
export async function refreshKeys(): Promise<number> {
  try {
    await initCrypto();
  } catch {
    return 0;
  }
  return drainToDevice();
}

export class UnableToDecryptError extends Error {
  constructor(readonly reason: 'no-session' | 'failed') {
    super(reason === 'no-session' ? 'No key for this message yet.' : 'Could not decrypt.');
    this.name = 'UnableToDecryptError';
  }
}

/**
 * Decrypt one Megolm message.
 *
 * The ratchet advances on every decrypt, so the returned pickle must be
 * persisted - dropping it means the session cannot read the next message.
 */
export async function decryptMessage(
  sessionId: string,
  ciphertext: string,
  /** Cache the result under this id so the chat list can preview it. */
  messageId?: string,
): Promise<string> {
  const pickle = await loadMegolmSession(sessionId);
  if (!pickle) throw new UnableToDecryptError('no-session');

  const core = await loadCrypto();
  let out;
  try {
    out = core.groupDecrypt(pickle, ciphertext);
  } catch {
    throw new UnableToDecryptError('failed');
  }
  await saveMegolmSession(sessionId, out.session_pickle);
  const body = unwrapPayload(out.plaintext);
  // Best effort: a cache write must never fail a decrypt.
  if (messageId) void savePlaintext(messageId, body).catch(() => undefined);
  return body;
}

/**
 * Unwrap the `{ type, content }` payload the sender produced.
 *
 * Must match `encryptMessage` on the phone exactly - it sends
 * `JSON.stringify({ type: "m.text", content })`. Falls back to the raw
 * string so a future payload shape degrades to showing something rather
 * than nothing.
 */
function unwrapPayload(raw: string): string {
  try {
    const obj = JSON.parse(raw) as { type?: string; content?: unknown };
    if (obj && typeof obj.content === 'string') return obj.content;
  } catch {
    /* not JSON - return as-is */
  }
  return raw;
}

/** Called on sign-out. Also resets the in-memory handle so a subsequent
 *  sign-in bootstraps a fresh identity rather than reusing a stale one. */
export async function resetCrypto(): Promise<void> {
  identity = null;
  starting = null;
  setKeyRequestHandler(null);
  await clearCryptoStore();
}

export { pickleKey };
